import React, {useEffect, useMemo, Fragment, useCallback} from "react";
import OrderComponent from "../../../models/OrderComponent";
import {Field, FieldProps} from "formik";
import DateInput from "../../orders/components/DateInput";
import ClientInput from "../../orders/components/ClientInput";
import VendorInput from "../../orders/components/VendorInput";
import ProfitInput from "../../orders/components/ProfitInput";
import CarTerminalInput from "../../orders/components/CarTerminalInput";
import {useAppDispatch, useAppSelector} from "../../../utils/Hooks";
import {getCountries} from "../../../store/reducers/CountriesSlice";
import {getServices} from "../../../store/reducers/ServicesSlice";
import {getUsers} from "../../../store/reducers/UsersSlice";
import ElogSelect, {Option} from "../../common/form/ElogSelect";
import ElogInput from "../../common/form/ElogInput";
import ElogTextarea from "../../common/form/ElogTextarea";
import ElogUpload from "../../common/form/ElogUpload";
import ElogCheckbox from "../../common/form/ElogCheckbox";
import OrderFile from "../../../models/OrderFile";

interface OrderFormProps {
    orderComponents: OrderComponent[];
    onSelectedServiceChange: (service: string) => void;
    clientContacts?: Option[];
    vendorContacts?: Option[];
    files?: OrderFile[];
    values?: any;
    taxDept?: boolean;
    withDeletedServices?: boolean,
    hideLogistician?: boolean
    isModify?: boolean
}

export const getOrderLogisticianName = (order: any, users: any[] = []) => {
    const orderUser = order?.user;
    const fallbackUser = users.find(user => user.id === order?.user_id);
    const firstname = orderUser?.firstname ?? fallbackUser?.firstname ?? '';
    const lastname = orderUser?.lastname ?? fallbackUser?.lastname ?? '';

    return `${firstname} ${lastname}`.trim();
};

const OrderForm: React.FC<OrderFormProps> = (
    {orderComponents, onSelectedServiceChange, clientContacts, vendorContacts, files, values, taxDept, withDeletedServices=false, hideLogistician=false, isModify=false}: OrderFormProps) => {

    const dispatch = useAppDispatch();

    const servicesState = useAppSelector((state) => state.services);
    const usersState = useAppSelector((state) => state.users);
    const inquiriesState = useAppSelector((state) => state.inquiries);
    const ordersState = useAppSelector((state) => state.orders);

    const serviceOptions = servicesState.services.map((service) => ({
        value: service.id.toString(),
        label: service.name,
    }));

    const userOptions = useMemo(() => {
        return usersState.users
            .filter((user) => user.is_checker === 1)
            .map((user) => ({
                value: user.id.toString(),
                label: `${user.firstname} ${user.lastname}`,
            }));
    }, [usersState.users]);

    useEffect(() => {
        dispatch(getCountries());
        dispatch(getServices(withDeletedServices));
        dispatch(getUsers());
    }, []);

    const goodsInvoices = useMemo(() => (files || []).filter(file => file?.type === 'goodsInvoice'), [files]);
    const packagingLists = useMemo(() => (files || []).filter(file => file?.type === 'packagingList'), [files]);
    const deliverySheets = useMemo(() => (files || []).filter(file => file?.type === 'deliverySheet'), [files]);
    const otherDocuments = useMemo(() => (files || []).filter(file => file?.type === 'otherDocument'), [files]);

    const renderComponent = useMemo(() => (component: OrderComponent) => {

        let label = component.name;
        if (component.pivot?.mandatory) {
            label = label + ' *';
        }

        if (component.component === 'LogisticianName') {

            if ( hideLogistician ) {
                return null;
            }

            if (!!ordersState?.currentOrder) {
                return (<tr key={'form-' + component.name}>
                    <th>{label}</th>
                    <td>
                        <strong>{getOrderLogisticianName(ordersState.currentOrder, usersState.users)}</strong>
                    </td>
                </tr>);
            }
        } else if (component.component === 'InvoiceSent') {
            if (!isModify) return null;
            if (!!ordersState?.currentOrder?.invoice_time) {
                return (<tr key={'form-' + component.name}>
                    <th>{label}</th>
                    <td><strong>{ordersState?.currentOrder.invoice_time}</strong></td>
                </tr>);
            }
        } else if (component.component === 'InquiryId') {
            if (!isModify) return null;
            if (!!ordersState?.currentOrder) {
                return (<tr key={'form-' + component.name}>
                    <th>{label}</th>
                    <td>
                        <strong>{ordersState?.currentOrder?.id}</strong>
                    </td>
                </tr>);
            }
        } else if (component.component === 'OrderDate') {

            return (<tr key={'form-' + component.name}>
                <th>{label}</th>
                <td>
                    <Field name="orderTime">
                        {({field}: FieldProps) => {
                            return (
                                <DateInput
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                />
                            )
                        }}
                    </Field>
                </td>
            </tr>);

        } else if (component.component === 'LoadingDate') {

            return (<tr key={'form-' + component.name}>
                <th>{label}</th>
                <td>
                    <Field name="loadingTime">
                        {({field}: FieldProps) => {
                            return (
                                <DateInput
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                />
                            )
                        }}
                    </Field>
                </td>
            </tr>);

        } else if (component.component === 'Client') {
            return (<Fragment key={'form-' + component.name}><ClientInput contacts={clientContacts}/></Fragment>);
        } else if (component.component === 'Vendor') {
            return (<Fragment key={'form-' + component.name}><VendorInput contacts={vendorContacts}/></Fragment>);
        } else if (component.component === 'Service') {
            return (<Fragment key={'form-' + component.name}><ElogSelect label={component.name} name="service"
                                                                         options={serviceOptions}
                                                                         mandatory={!!component.pivot?.mandatory}
                                                                         onChange={onSelectedServiceChange}
            /></Fragment>)
        } else if (component.component === 'BuySum') {
            return (<Fragment key={'form-' + component.name}><ElogInput label={component.name} name={'buySum'}
                                                                        type={'number'}
                                                                        mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'SaleSum') {
            return (<Fragment key={'form-' + component.name}><ElogInput label={component.name} name={'saleSum'}
                                                                        type={'number'}
                                                                        mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'Profit') {
            return (<Fragment key={'form-' + component.name}><ProfitInput label={component.name}/></Fragment>)
        } else if (component.component === 'BuyPaid') {
            return (<Fragment key={'form-' + component.name}><ElogCheckbox label={component.name}
                                                                           name={'buyPaid'}/></Fragment>);
        } else if (component.component === 'SalePaid') {
            return (<Fragment key={'form-' + component.name}><ElogCheckbox label={component.name}
                                                                           name={'salePaid'}/></Fragment>);
        } else if (component.component === 'BuyInvoice') {
            return (<Fragment key={'form-' + component.name}><ElogInput label={component.name} name={'buyInvoice'}
                                                                        mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'SaleInvoice') {
            return (<Fragment key={'form-' + component.name}><ElogInput label={component.name} name={'saleInvoice'}
                                                                        mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'Website') {
            return (<Fragment key={'form-' + component.name}><ElogCheckbox label={component.name}
                                                                           name={'website'}/></Fragment>);
        } else if (component.component === 'Loaded') {
            return (<Fragment key={'form-' + component.name}><ElogCheckbox label={component.name}
                                                                           name={'loaded'}/></Fragment>);
        } else if (component.component === 'Unloaded') {
            return (<Fragment key={'form-' + component.name}><ElogCheckbox label={component.name}
                                                                           name={'unloaded'}/></Fragment>);
        } else if (component.component === 'DocumentsSent') {
            return (<Fragment key={'form-' + component.name}><ElogCheckbox label={component.name}
                                                                           name={'documentsSent'}/></Fragment>);
        } else if (component.component === 'CMR') {
            return (<Fragment key={'form-' + component.name}><ElogCheckbox label={component.name}
                                                                           name={'cmr'}/></Fragment>);
        } else if (component.component === 'Reclamation') {
            return (<Fragment key={'form-' + component.name}><ElogCheckbox label={component.name} name={'reclamation'}/></Fragment>);
        } else if (component.component === 'RefNumber') {
            return (<Fragment key={'form-' + component.name}><ElogInput label={component.name} name={'refNumber'}
                                                                        mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'Car') {
            return (<Fragment key={'form-' + component.name}><CarTerminalInput/></Fragment>)
        } else if (component.component === 'LoadingInstruction') {
            return (<Fragment key={'form-' + component.name}><ElogTextarea label={component.name}
                                                                           name={'loadingInstructions'}
                                                                           mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'UnloadingInstruction') {
            return (<Fragment key={'form-' + component.name}><ElogTextarea label={component.name}
                                                                           name={'unloadingInstructions'}
                                                                           mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'Amount') {
            return (<Fragment key={'form-' + component.name}><ElogTextarea label={component.name}
                                                                           name={'amount'}
                                                                           mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'Weight') {
            return (<Fragment key={'form-' + component.name}><ElogInput label={component.name}
                                                                        name={'weight'}
                                                                        mandatory={!!component.pivot?.mandatory}
                                                                        classes={'small'}
                                                                        unit={'kg'}/></Fragment>)
        } else if (component.component === 'Background') {
            return (<Fragment key={'form-' + component.name}></Fragment>);
        } else if (component.component === 'Comment') {
            return (<Fragment key={'form-' + component.name}>{taxDept && <tr>
              <th></th>
              <td><p style={{color: 'red', fontWeight: 'bold'}}>
                Maksuvõlg
              </p></td>
            </tr>
            }<ElogTextarea label={component.name}
                           name={'comment'}
                           mandatory={!!component.pivot?.mandatory}/></Fragment>)
        } else if (component.component === 'Checker') {
            return (<Fragment key={'form-' + component.name}><ElogSelect label="Checker" name="checker"
                                                                         options={userOptions}
                                                                         mandatory={!!component.pivot?.mandatory}/></Fragment>);
        } else if (component.component === 'Meters') {
            return (<Fragment key={'form-' + component.name}><ElogInput label={component.name}
                                                                        name={'meters'}
                                                                        mandatory={!!component.pivot?.mandatory}
                                                                        classes={'small'}
                                                                        unit={'ldm'}/></Fragment>)
        } else if (['GoodsInvoice', 'PackagingList', 'DeliverySheet', 'OtherDocuments'].indexOf(component.component)
            > -1) {

            const name = component.component.charAt(0).toLowerCase() + component.component.slice(1);
            const uploadedFiled: OrderFile[] = (files || []).filter(file => file.type === name);

            return (<Fragment key={'form-' + component.name}><ElogUpload label={component.name} name={name}
                                                                         files={uploadedFiled}/></Fragment>);

        }

        return null;

    }, [
        clientContacts,
        files,
        hideLogistician,
        isModify,
        onSelectedServiceChange,
        ordersState,
        serviceOptions,
        taxDept,
        userOptions,
        usersState.users,
        vendorContacts,
    ]);


    const renderForm = useCallback(() => {
        return orderComponents.filter(component => component.component !== "Loadings").map(component => renderComponent(component));
    }, [orderComponents, renderComponent]);

    return (
        <tbody>
        {renderForm()}
        </tbody>
    )

}

export default OrderForm;
